import Section from 'app/frontend/base/components/Section'
import { useTeam } from 'app/frontend/base/config/context/team'
import AuditTable from './components/AuditTable'
import AuditCategory, {
  auditCategories,
} from 'app/frontend/types/custom/AuditCategory'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import { usePageInfo, useTeamAuditData } from 'app/frontend/base/config/queries'
import { AuditContext } from './context'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useAuditFilterState } from './components/AuditFilter'
import { Button } from '@/components/ui/button'
import { RefreshCw, Table } from 'lucide-react'
import SpreadsheetDialog from './components/SpreadsheetDialog'
import RefreshStatusDialog from './components/RefreshStatusDialog'

const TeamAudit = () => {
  const {
    data: { currentSeason },
  } = usePageInfo()

  const { team, guild } = useTeam()
  const { hash: initialCategory } = useLocation()
  const [, setSearchParams] = useSearchParams()
  const [selectedSeason, setSelectedSeason] = useState(currentSeason)
  const { value: weeksAgoParam, setValue: setWeeksAgoParam } =
    useAuditFilterState<string>('weeksAgo', '0')
  const weeksAgo = Number(weeksAgoParam) || 0
  const setWeeksAgo = (value: number) => setWeeksAgoParam(String(value))
  const [activeView, setActiveView] = useState<'graph' | 'list'>('list')
  const [spreadsheetOpen, setSpreadsheetOpen] = useState(false)
  const [refreshStatusOpen, setRefreshStatusOpen] = useState(false)
  const [showingRanks, setShowingRanksState] = useState(() => {
    const stored = localStorage.getItem('audit-showing-ranks')
    return stored ? JSON.parse(stored) : false
  })

  const setShowingRanks = (value: boolean) => {
    setShowingRanksState(value)
    localStorage.setItem('audit-showing-ranks', JSON.stringify(value))
  }

  const [activeCategory, setActiveCategoryState] = useState<AuditCategory>(
    () => {
      const fromHash = initialCategory?.replace('#', '')
      if (fromHash && auditCategories.includes(fromHash as AuditCategory)) {
        return fromHash as AuditCategory
      }

      const stored = localStorage.getItem(
        'audit-filter-activeCategory',
      ) as AuditCategory | null
      if (stored && auditCategories.includes(stored)) {
        return stored
      }

      return 'summary'
    },
  )

  const setActiveCategory = (category: AuditCategory) => {
    setActiveCategoryState(category)
    localStorage.setItem('audit-filter-activeCategory', category)
  }
  const { t } = useTranslation()

  const { data } = useTeamAuditData(team.id)

  const visibleMembers = useMemo(() => {
    return (
      team.members?.filter(
        (member) => member.teamRank?.spreadsheet_overview_visibility,
      ) ?? []
    ).map((member) => ({
      ...member,
      data: data?.[member.characterReference.id]?.last_refresh,
      gear: data?.[member.characterReference.id]?.current_gear,
      keystones: data?.[member.characterReference.id]?.keystones,
      mythicRatingColor:
        data?.[member.characterReference.id]?.mythic_rating_color,
      timeline: data?.[member.characterReference.id]?.timeline,
    }))
  }, [data, team.members])

  return (
    <Section title={`Audit | ${team.name} | ${guild.name}`}>
      <h2 className='flex items-center justify-between'>
        <span>Audit</span>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setSpreadsheetOpen(true)}
          >
            <Table className='h-4 w-4 mr-2' />
            View in spreadsheet
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setRefreshStatusOpen(true)}
          >
            <RefreshCw className='h-4 w-4 mr-2' />
            Refresh status
          </Button>
        </div>
      </h2>

      <SpreadsheetDialog open={spreadsheetOpen} setOpen={setSpreadsheetOpen} />
      <RefreshStatusDialog
        open={refreshStatusOpen}
        setOpen={setRefreshStatusOpen}
      />

      <Tabs
        value={activeCategory}
        onValueChange={(value) => {
          const newCategory = value as AuditCategory
          setActiveCategory(newCategory)
          setWeeksAgo(0)
          setSearchParams(
            (prev) => {
              prev.delete('sorting')
              return prev
            },
            { replace: true },
          )
          history.replaceState({}, '', `#${newCategory}`)
        }}
      >
        <TabsList className='w-full bg-transparent hover:bg-transparent flex justify-start gap-2 flex-wrap border-0'>
          {auditCategories.map((category) => (
            <TabsTrigger
              key={category}
              value={category}
              className='flex flex-col items-center text-foreground hover:bg-accent justify-center bg-card h-12 grow border rounded-md px-4 max-w-40 data-[state=active]:border-primary data-[state=active]:bg-card data-[state=active]:text-primary'
            >
              <div>{t(`audit.categories.${category}.label`)}</div>
              <div className='text-xs text-muted-foreground font-normal'>
                {t(`audit.categories.${category}.caption`)}
              </div>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <AuditContext.Provider
        value={{
          selectedSeason,
          setSelectedSeason,
          visibleMembers,
          weeksAgo,
          setWeeksAgo,
          showingRanks,
          setShowingRanks,
          activeView,
          setActiveView,
        }}
      >
        <AuditTable key={activeCategory} category={activeCategory} />
      </AuditContext.Provider>
    </Section>
  )
}

export default TeamAudit
